import React, {
  useRef, useEffect, useState, useCallback
} from "react"
import { setupPdfJs } from "../../utils/pdfSetup"
import AnnotationCanvasSimple from "./AnnotationCanvasSimple"
import styles from "./PdfToolbar.module.css"

export default function PdfCanvas({
  pdfUrl,
  annotations = [],
  onAnnotationChange,
  canAnnotate = false,
  currentTool = "select",
  currentPage = 1,
  onPageChange,
  selectedStampType = "approved",
  toolSettings = null,
  selectedAnnotationId = null,
  onSelectionChange = null,
  userSignatureAsset = null,
  userInfo = null
}) {
  const canvasRef = useRef(null)
  const pdfDocRef = useRef(null)
  const [pdfDoc, setPdfDoc] = useState(null)
  const [pageCount, setPageCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  // Render current page
  const renderPage = useCallback(async pageNum => {
    if (!pdfDoc || !canvasRef.current) return

    try {
      // eslint-disable-next-line no-console
      console.log(`[PDF RENDER] Starting render of page ${pageNum}`)
      
      const page = await pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: 1.5 })
      
      // Update canvas size
      const canvas = canvasRef.current
      const context = canvas.getContext("2d")
      canvas.width = viewport.width
      canvas.height = viewport.height
      
      // Store dimensions for annotation canvas
      setCanvasSize({ width: viewport.width, height: viewport.height })

      // Clear the canvas first
      context.clearRect(0, 0, canvas.width, canvas.height)

      // Render PDF page
      await page.render({
        canvasContext: context,
        viewport
      }).promise

      // eslint-disable-next-line no-console
      console.log(`[PDF RENDER] Completed render of page ${pageNum}`)
      
      setError(null)
    } catch (err) {
      setError(`Failed to render page: ${err.message}`)
    }
  }, [pdfDoc])

  // Load PDF document
  useEffect(() => {
    if (!pdfUrl) return

    setLoading(true)
    setError(null)

    const loadPdfWithAuth = async () => {
      try {
        const pdfjsLib = setupPdfJs()
        
        // Check if authentication token is available
        if (!gon || !gon.auth_token) {
          throw new Error("Authentication token is missing. Please refresh the page and sign in again.")
        }
        
        // Fetch the PDF with authentication header
        const pdfResponse = await fetch(pdfUrl, {
          headers: {
            Authorization: gon.auth_token
          }
        })
        
        if (!pdfResponse.ok) {
          if (pdfResponse.status === 403) {
            throw new Error("You do not have permission to view this PDF.")
          }
          if (pdfResponse.status === 404) {
            throw new Error("PDF file not found.")
          }
          throw new Error(`HTTP ${pdfResponse.status}: ${pdfResponse.statusText}`)
        }
        
        // Get the PDF as array buffer
        const pdfArrayBuffer = await pdfResponse.arrayBuffer()
        
        // Load PDF from array buffer
        const pdf = await pdfjsLib.getDocument({
          data: pdfArrayBuffer
        }).promise
        
        pdfDocRef.current = pdf
        setPdfDoc(pdf)
        setPageCount(pdf.numPages)
        setLoading(false)
      } catch (err) {
        setError(`Failed to load PDF: ${err.message}`)
        setLoading(false)
      }
    }

    loadPdfWithAuth()
  }, [pdfUrl])

  // Handle page change
  useEffect(() => {
    if (pdfDoc && currentPage >= 1 && currentPage <= pageCount && !loading) {
      // Add a small delay to ensure canvas is rendered
      const timer = setTimeout(() => {
        renderPage(currentPage)
      }, 10)
      
      return () => clearTimeout(timer)
    }
    
    return undefined
  }, [pdfDoc, currentPage, pageCount, renderPage, loading])

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "500px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading PDF...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h6>PDF Rendering Error</h6>
        <p>{error}</p>
        <small className="text-muted">
          Make sure the PDF URL is accessible and the file exists.
        </small>
      </div>
    )
  }

  return (
    <div className={styles.pdfCanvasContainer}>
      {/* Page navigation */}
      {pageCount > 1 && (
        <div className="pdf-page-navigation mb-2 d-flex justify-content-between align-items-center p-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage <= 1}
            onClick={() => onPageChange && onPageChange(currentPage - 1)}>
            Previous
          </button>
          
          <span className="page-info">
            Page
            {" "}
            {currentPage}
            {" "}
            of
            {" "}
            {pageCount}
          </span>
          
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage >= pageCount}
            onClick={() => onPageChange && onPageChange(currentPage + 1)}>
            Next
          </button>
        </div>
      )}

      {/* Canvas */}
      <div className={`${styles.pdfCanvasWrapper} border position-relative`}>
        <canvas
          ref={canvasRef}
          style={{ display: "block" }} />
        
        {/* Interactive annotation layer */}
        {canAnnotate && pdfDoc && canvasSize.width > 0 && canvasSize.height > 0 && (
          <AnnotationCanvasSimple
            width={canvasSize.width}
            height={canvasSize.height}
            annotations={annotations}
            onAnnotationsChange={onAnnotationChange}
            currentTool={currentTool}
            currentPage={currentPage}
            canAnnotate={canAnnotate}
            selectedStampType={selectedStampType}
            toolSettings={toolSettings}
            selectedAnnotationId={selectedAnnotationId}
            onSelectionChange={onSelectionChange}
            userSignatureAsset={userSignatureAsset}
            userInfo={userInfo} />
        )}
        
        {/* Annotation count indicator */}
        {canAnnotate && annotations.length > 0 && (
          <small
            className="text-muted position-absolute"
            style={{
              bottom: 10,
              right: 10,
              background: "rgba(255,255,255,0.8)",
              padding: "2px 6px",
              borderRadius: 3,
              pointerEvents: "none"
            }}>
            {annotations.length}
            {" "}
            annotation(s)
          </small>
        )}
      </div>
      
      {/* Status info */}
      <div className="pdf-status mt-2">
        <small className="text-muted">
          <strong>Status:</strong>
          {" "}
          PDF loaded successfully
          {canAnnotate && " • Annotations enabled"}
          {annotations.length > 0 && ` • ${annotations.length} annotation(s)`}
        </small>
      </div>
    </div>
  )
}
